import * as queryString from 'query-string';
import React, { Component } from 'react';
import { Dispatch } from 'redux';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import { selectPopUpsPlansAndPricingUpgradeInfo } from '../../PopUps/selectors/PopUpsSelectors';
import { ContentClass } from '../../app/Shared/enums';
import {
  selectIsLoggedIn,
  selectIsMobile,
  selectMakerHooksVisible,
  selectPrimarySubscription,
  selectSubscription,
} from '../../auth/AuthSelectors';
import { Subscription, PlanClassification } from '../../auth/AuthTypes';
import Logger from '../../common/Logger';
import SiteConstants from '../../common/SiteConstants/SiteConstants';
import FavoriteButton from '../../common/components/FavoriteButton';
import {
  StockItem,
  StockItemDetails,
  StockItemFormat,
} from '../../common/types/StockItemTypes';
import { AUDIO, getPreviewUrl, ucfirst } from '../../common/utils';
import MakerEditorActionsHook from '../components/MakerEditorActionsHook';
import MakerEditorHook from '../components/MakerEditorHook';
import StockItemDownloadButton from '../components/StockItemDownloadButton';
import MemberDetailsDownloadInfo from '../components/download/cta/MemberDetailsDownloadInfo';
import MemberDownloadCtaDisclaimer from '../components/download/cta/MemberDownloadCtaDisclaimer';
import MemberDownloadUpgradeCTA from '../components/download/cta/MemberDownloadUpgradeCTA';
import VisitorSignUpButton from '../components/download/cta/VisitorSignUpButton';
import DetailsMessaging from '../components/download/messaging/DetailsMessaging';
import {
  PAYWALL_DOWNLOAD_BLOCKED_TAG,
  PAYWALL_PRICING_LINK_TAG,
} from '../constants';
import {
  selectDetailsStockItemFormats,
  selectDetailsStockItem,
  selectDetailsDownloadPermissions,
  selectDetailsCanDownload,
  selectDetailsShouldShowAddToFavorites,
  selectCanDownloadPreviews,
  selectDetailsMakerPreCreateProjectRoute,
  selectDetailsMakerSignupRoute,
  selectSimilarStockItems,
  selectDetailsAcquisitionOption,
  selectMvtPages,
  selectDetailsIsEnterpriseMember,
  selectCanDownloadNonWatermarkedComps,
  selectDetailsSignUpLink,
} from '../selectors/detailsSelectors';

import './details-actions.less';
import './stock-item-details.less';

export interface AbstractItemDetailsContainerProps {
  stockItem: StockItem;
  stockItemFormats: StockItemFormat[];
  canDownload: boolean;
  downloadPermissions: Record<string, boolean>;
  shouldShowAddToFavorites: boolean;
  isLoggedIn: boolean;
  subscription: Subscription;
  primarySubscription: Subscription;
  isMobile: boolean;
  canDownloadPreviews: boolean;
  makerPreCreateProjectRoute: string;
  makerSignupRoute: string;
  hasSubscription: boolean;
  hasOtherSubscriptions: boolean;
  selectedAcquisitionOption: StockItemFormat;
  shouldShowMusicRecommendations: boolean;
  similarStockItems: StockItem[];
  mvtPages: object;
  isEnterpriseMember: boolean;
  canDownloadNonWatermarkedComps: boolean;
  popUpsPlansAndPricingUpgradeInfo: object;
  musicRecommendations: StockItemDetails[];
  dispatch: Dispatch;
  siteSubscription: boolean;
}

class AbstractItemDetailsContainer<
  P extends
    AbstractItemDetailsContainerProps = AbstractItemDetailsContainerProps,
> extends Component<P> {
  async downloadNonWatermarkedPreview() {
    const dataBridge = await SiteConstants.buildInstance().getDataBridge();

    dataBridge.downloadItem(
      this.props.selectedAcquisitionOption.downloadUrl,
      this.props.selectedAcquisitionOption.downloadAjaxUrl,
      null,
      this.props.dispatch,
      null
    );
  }

  downloadWatermarkedPreview() {
    window.location.href = getPreviewUrl(
      this.props.stockItem.id,
      this.props.stockItem.contentClass
    );
  }

  downloadWithOrWithoutWatermarks() {
    return this.props.isEnterpriseMember &&
      this.props.canDownloadNonWatermarkedComps
      ? this.downloadNonWatermarkedPreview()
      : this.downloadWatermarkedPreview();
  }

  componentDidMount() {
    if (
      this.props.canDownloadPreviews &&
      window.location.search === '?preview=1'
    ) {
      return this.downloadWithOrWithoutWatermarks();
    }
  }

  buildDownloadCta(contentTypesString, contentClass: ContentClass) {
    if (this.props.canDownload) {
      return this.buildMemberDownloadCta(contentTypesString, contentClass);
    } else {
      return this.buildNonSubscriberDownloadCta(contentClass);
    }
  }

  buildMakerHook() {
    return <MakerEditorHook makerEditorLink={this.generateMakerLink()} />;
  }

  buildMakerActionsHook(contentClass) {
    return (
      <MakerEditorActionsHook
        makerEditorLink={this.generateMakerLink()}
        contentClass={contentClass}
        stockItemId={this.props.stockItem.id}
      />
    );
  }

  buildMemberDownloadCta(contentTypesString, contentClass: ContentClass) {
    return (
      <>
        <MemberDownloadUpgradeCTA
          primaryActionHandler={this.buildMemberDownloadHandler()}
        >
          <StockItemDownloadButton />
          {this.props.shouldShowAddToFavorites && (
            <FavoriteButton
              buttonClasses="relative p-1 mx-2 my-0 text-xs bg-gray-100 border-none rounded uppercase"
              buttonText="Add to Folder"
              stockItem={this.props.stockItem}
              stockItemFormats={this.props.stockItemFormats}
              telemetryKey="details"
              iconClassName="w-4 h-4"
              alternateIconDefaultColor="text-black"
              alternateIconInFolderColor="text-black"
            />
          )}
        </MemberDownloadUpgradeCTA>
        {this.shouldShowMakerHook(contentClass) && this.buildMakerHook()}
        <MemberDownloadCtaDisclaimer
          needsToUpgrade={this.upgradeRequiredForMember()}
        />
        <MemberDetailsDownloadInfo
          needsToUpgrade={this.upgradeRequiredForMember()}
          contentTypesString={contentTypesString}
        />
      </>
    );
  }

  async memberDownloadHandler() {
    Logger.produceDownloadClickEvent(this.props.stockItem);

    const dataBridge = await SiteConstants.buildInstance().getDataBridge();

    dataBridge.downloadItem(
      queryString.stringifyUrl(
        {
          url: this.props.selectedAcquisitionOption.downloadUrl,
        },
        {
          skipNull: true,
        }
      ),
      queryString.stringifyUrl(
        {
          url: this.props.selectedAcquisitionOption.downloadAjaxUrl,
        },
        {
          skipNull: true,
        }
      ),
      undefined,
      this.props.dispatch,
      // NOTE: used to provide data for Intercom in Starter Plan A/B Test, remove after test if possible
      this.props.primarySubscription || this.props.subscription
    );
  }

  buildMemberDownloadHandler() {
    return (event) => {
      Telemetry.increment('download.clicked.details');
      if (event.type === 'keydown' && event.key !== 'Enter') {
        if (event.key === ' ') {
          event.preventDefault();
        }
        return;
      }
      this.memberDownloadHandler();
    };
  }

  buildPaywallBlockedDownloadHandler() {
    return () => {
      Telemetry.increment(PAYWALL_DOWNLOAD_BLOCKED_TAG);
    };
  }

  buildNonSubscriberDownloadCta(contentClass: ContentClass) {
    const cta = this.props.hasOtherSubscriptions
      ? 'Add ' + ucfirst(contentClass) + ' Plan to Download this Content'
      : this.props.isLoggedIn
      ? 'Subscribe to Download'
      : 'Sign Up to Download';
    return (
      <VisitorSignUpButton
        makerEditorLink={this.generateMakerLink()}
        signUpButtonCta={cta}
        contentClass={contentClass}
        onSignUpClick={this.buildPaywallBlockedDownloadHandler()}
      >
        <StockItemDownloadButton />
      </VisitorSignUpButton>
    );
  }

  getDownloadsRemainingMessaging() {
    const { primarySubscription } = this.props;
    const hasMakerOrStarter =
      primarySubscription?.planClassification ===
        PlanClassification.STARTER_PLAN_CLASSIFICATION ||
      primarySubscription?.isMakerOnly;
    const hasDownloadsAvailable =
      primarySubscription?.monthlyDownloadsRemaining &&
      primarySubscription?.status?.toLowerCase?.() !== 'cancel';
    return hasMakerOrStarter && hasDownloadsAvailable
      ? `${primarySubscription.monthlyDownloadsRemaining} downloads remaining this month`
      : '';
  }

  buildMemberLibraryMessaging(title = '') {
    const { canDownload, hasSubscription, primarySubscription } = this.props;

    const hasMakerOrStarter =
      primarySubscription?.planClassification ===
        PlanClassification.STARTER_PLAN_CLASSIFICATION ||
      primarySubscription?.isMakerOnly;
    const hasDownloadsAvailable =
      primarySubscription?.monthlyDownloadsRemaining &&
      primarySubscription?.status?.toLowerCase?.() !== 'cancel';

    if (
      (hasSubscription && !canDownload) ||
      (hasSubscription && this.upgradeRequiredForMember()) ||
      (hasMakerOrStarter && !hasDownloadsAvailable)
    ) {
      title = 'Upgrade To Download';
    } else if (hasMakerOrStarter && hasDownloadsAvailable) {
      title = this.getDownloadsRemainingMessaging();
    } else if (hasSubscription) {
      title = 'Included in your subscription';
    } else {
      title = 'Unlock unlimited downloads with a subscription plan';
    }

    let message: JSX.Element = <></>;
    if (!hasSubscription) {
      message = (
        <div>
          Get access to our huge library of stock footage, music, After Effects
          templates, images, sound effects, and more with our{' '}
          <a
            href="/pricing"
            className="text-blue-1000 hover:text-blue-500"
            onClick={() => Telemetry.increment(PAYWALL_PRICING_LINK_TAG)}
          >
            flexible plan options
          </a>
        </div>
      );
    }

    const divider = hasSubscription ? null : (
      <hr className="stockItemDetails-divider" />
    );

    return (
      <div data-cta="plan_options_message">
        <DetailsMessaging>
          {{
            title,
            message,
          }}
        </DetailsMessaging>
        {divider}
      </div>
    );
  }

  buildAssetId() {
    const { isLoggedIn, stockItem } = this.props;

    if (!isLoggedIn) return null;

    return <div className="mt-4">Asset ID: {stockItem.assetId}</div>;
  }

  generateMakerLink() {
    return this.props.isLoggedIn
      ? this.props.makerPreCreateProjectRoute
      : this.props.makerSignupRoute;
  }

  shouldShowMakerHook(contentClass) {
    return !this.props.isMobile && contentClass !== AUDIO;
  }

  upgradeRequiredForMember() {
    return false;
  }
}

export function commonStateToProps(state) {
  return {
    stockItem: selectDetailsStockItem(state),
    stockItemFormats: selectDetailsStockItemFormats(state),
    canDownload: selectDetailsCanDownload(state),
    mvtPages: selectMvtPages(state),
    similarStockItems: selectSimilarStockItems(state),
    downloadPermissions: selectDetailsDownloadPermissions(state),
    selectedAcquisitionOption: selectDetailsAcquisitionOption(state),
    shouldShowAddToFavorites: selectDetailsShouldShowAddToFavorites(state),
    isLoggedIn: selectIsLoggedIn(state),
    makerHooksVisible: selectMakerHooksVisible(state),
    subscription: selectSubscription(state),
    primarySubscription: selectPrimarySubscription(state),
    isMobile: selectIsMobile(state),
    canDownloadPreviews: selectCanDownloadPreviews(state),
    makerPreCreateProjectRoute: selectDetailsMakerPreCreateProjectRoute(state),
    makerSignupRoute: selectDetailsMakerSignupRoute(state),
    isEnterpriseMember: selectDetailsIsEnterpriseMember(state),
    canDownloadWatermarkComps: selectCanDownloadNonWatermarkedComps(state),
    popUpsPlansAndPricingUpgradeInfo:
      selectPopUpsPlansAndPricingUpgradeInfo(state),
    signUpLink: selectDetailsSignUpLink(state),
  };
}

export default AbstractItemDetailsContainer;
